import { API_URL, DEV_SERVER_API_URL } from '@/constants/constants';
import { ApiError, ForbiddenError, NetworkError, UnauthorizedError, ValidationError } from '@/utils/errors';

export async function apiFetch(path: string, options: RequestInit = {}) {
    let response: Response;
    try {
        response = await fetch(`${API_URL}${path}`, {
            ...options,
            credentials: 'include',
            cache: 'no-store',
        });
    } catch (error) {
        throw new NetworkError(`Network Error at ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }

    let data;
    try {
        data = await response.json();
    } catch (jsonError) {
        void jsonError;
        data = null;
    }

    if (!response.ok) {
        const detail = data?.detail;
        switch (response.status) {
            case 400:
                if (Array.isArray(detail)) {
                    throw new ValidationError(JSON.stringify(detail));
                }
                throw new ValidationError(typeof detail === 'string' ? detail : 'Validation error');
            case 401:
                throw new UnauthorizedError();
            case 403:
                throw new ForbiddenError();
            default:
                throw new ApiError(data?.detail || 'unknown error.');
        }
    }

    return Object.assign(data, {
        __headers: response.headers,
    });
}

export const apiFetchWithQuery = (
    path: string,
    params: Record<string, string | number | boolean | undefined | null>,
    options?: RequestInit,
) => {
    const filtered = Object.entries(params).filter(([, v]) => v !== undefined && v !== null);
    const queryString = new URLSearchParams(filtered as [string, string][]).toString();
    const fullPath = `${path}?${queryString}`;
    return apiFetch(fullPath, options);
};

export async function apiServerFetch(path: string, options: RequestInit = {}) {
    const baseUrl =
        process.env.NODE_ENV === 'development' ? process.env.DEV_SERVER_API_URL : process.env.NEXT_PUBLIC_API_URL;
    if (!baseUrl) throw new Error('server-api-url is not set.');

    let response: Response;
    try {
        response = await fetch(`${baseUrl}${path}`, {
            ...options,
            credentials: 'include',
            cache: 'no-store',
        });
    } catch (error) {
        throw new NetworkError(`Network Error at ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }

    const setCookieHeaders = response.headers.getSetCookie
        ? response.headers.getSetCookie()
        : response.headers.get('set-cookie');

    const headers = new Headers(response.headers);

    if (setCookieHeaders) {
        if (Array.isArray(setCookieHeaders)) {
            setCookieHeaders.forEach((cookie) => headers.append('Set-Cookie', cookie));
        } else {
            headers.append('Set-Cookie', setCookieHeaders);
        }
    }

    let data;
    try {
        data = await response.json();
    } catch (jsonError) {
        void jsonError;
        data = null;
    }

    return {
        headers,
        status: response.status,
        data: data,
    };
}

export interface StreamCallbacks<T = any> {
    onProgress?: (data: any) => void;
    onComplete?: (data: T) => void;
    onError?: (error: Error) => void;
}

export async function apiStreamFetch<T = any>(
    path: string,
    options: RequestInit & { body?: string } = {},
    callbacks: StreamCallbacks<T> = {},
): Promise<void> {
    const { onProgress, onComplete, onError } = callbacks;

    try {
        const response = await fetch(`${API_URL}${path}`, {
            ...options,
            credentials: 'include',
            headers: {
                ...options.headers,
                Accept: 'text/event-stream',
            },
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                errorData = null;
            }

            const detail = errorData?.detail;
            switch (response.status) {
                case 400:
                    if (Array.isArray(detail)) {
                        throw new ValidationError(JSON.stringify(detail));
                    }
                    throw new ValidationError(typeof detail === 'string' ? detail : 'Validation error');
                case 401:
                    throw new UnauthorizedError();
                case 403:
                    throw new ForbiddenError();
                default:
                    throw new ApiError(detail || 'unknown error.');
            }
        }

        if (!response.body) {
            throw new NetworkError('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const jsonStr = line.slice(6);
                    try {
                        const data = JSON.parse(jsonStr);

                        if (data.done || data.complete || data.success !== undefined) {
                            onComplete?.(data);
                        } else {
                            onProgress?.(data);
                        }
                    } catch (parseError) {
                        console.warn('[apiStreamFetch] Failed to parse SSE data:', jsonStr);
                    }
                }
            }
        }
    } catch (error) {
        if (error instanceof Error) {
            onError?.(error);
        } else {
            onError?.(new NetworkError(String(error)));
        }
        throw error;
    }
}
